import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart } from '@mui/x-charts/BarChart';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { getAuthHeaders } from '../../../utils/authUtils';
import { createApiUrl } from '../../../utils/apiConfig';
import { useSnackbar } from '../../CentralSnackbar';
import { useFormatConfig, formatDateSync } from '../../../utils/dateTimeFormat';

const Cases = forwardRef(({ currentUser }, ref) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const { isLoaded: isFormatConfigLoaded } = useFormatConfig();
  const chartRef = useRef(null);
  
  // State
  const [fallzahlenData, setFallzahlenData] = useState(null);
  const [fallzahlenLoading, setFallzahlenLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState('month'); // Default: Monat
  const [startPeriod, setStartPeriod] = useState('');
  const [endPeriod, setEndPeriod] = useState('');
  const [allUsers, setAllUsers] = useState([]); // Alle verfügbaren User
  const [selectedUsers, setSelectedUsers] = useState([]); // Ausgewählte User
  
  // Kategorien-Toggles
  const [showMyReports, setShowMyReports] = useState(true);
  const [showForeignSignedReports, setShowForeignSignedReports] = useState(true);
  const [showIndicationApprovals, setShowIndicationApprovals] = useState(true);
  const [showReleasedReports, setShowReleasedReports] = useState(true);
  
  // Highlighting State
  const [highlightedSeries, setHighlightedSeries] = useState(null);

  // Helper: Get current period based on timeFilter
  const getCurrentPeriod = (filterType) => {
    const now = new Date();
    switch (filterType) {
      case 'date':
        return now.toISOString().split('T')[0];
      case 'week': {
        const year = now.getFullYear();
        const oneJan = new Date(year, 0, 1);
        const numberOfDays = Math.floor((now - oneJan) / (24 * 60 * 60 * 1000));
        const weekNumber = Math.ceil((numberOfDays + oneJan.getDay() + 1) / 7);
        return `${year}-W${String(weekNumber).padStart(2, '0')}`;
      }
      case 'month':
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      case 'quarter': {
        const quarter = Math.ceil((now.getMonth() + 1) / 3);
        return `${now.getFullYear()}-Q${quarter}`;
      }
      case 'year':
        return `${now.getFullYear()}`;
      default:
        return '';
    }
  };

  // Helper: Get next/previous period
  const getAdjacentPeriod = (period, filterType, direction) => {
    if (!period) return period;
    
    switch (filterType) {
      case 'date': {
        const date = new Date(period);
        date.setDate(date.getDate() + direction);
        return date.toISOString().split('T')[0];
      }
      case 'week': {
        const [year, week] = period.split('-W').map(Number);
        let newWeek = week + direction;
        let newYear = year;
        
        if (newWeek < 1) {
          newYear--;
          newWeek = 52;
        } else if (newWeek > 52) {
          newYear++;
          newWeek = 1;
        }
        return `${newYear}-W${String(newWeek).padStart(2, '0')}`;
      }
      case 'month': {
        const [year, month] = period.split('-').map(Number);
        let newMonth = month + direction;
        let newYear = year;
        
        if (newMonth < 1) {
          newYear--;
          newMonth = 12;
        } else if (newMonth > 12) {
          newYear++;
          newMonth = 1;
        }
        return `${newYear}-${String(newMonth).padStart(2, '0')}`;
      }
      case 'quarter': {
        const [year, quarter] = period.split('-Q').map(Number);
        let newQuarter = quarter + direction;
        let newYear = year;
        
        if (newQuarter < 1) {
          newYear--;
          newQuarter = 4;
        } else if (newQuarter > 4) {
          newYear++;
          newQuarter = 1;
        }
        return `${newYear}-Q${newQuarter}`;
      }
      case 'year': {
        const year = parseInt(period);
        return `${year + direction}`;
      }
      default:
        return period;
    }
  };

  // Expose download functions to parent component via ref
  useImperativeHandle(ref, () => ({
    handlePdfDownload,
    handleCsvDownload
  }));

  // Set default period on mount
  useEffect(() => {
    const currentPeriod = getCurrentPeriod(timeFilter);
    setStartPeriod(currentPeriod);
    setEndPeriod(currentPeriod);
  }, []);

  // Load Users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Load Fallzahlen data when selectedUsers change
  useEffect(() => {
    if (selectedUsers.length > 0) {
      loadFallzahlenData();
    }
  }, [selectedUsers]);

  // Add event listeners to bars for highlighting
  useEffect(() => {
    // Timeout damit das Chart Zeit hat zu rendern
    const timeout = setTimeout(() => {
      if (!chartRef.current) return;

      const handleBarMouseEnter = (event) => {
        const target = event.currentTarget;
        
        // Hole die Farbe (fill) des angeklickten Balkens
        const targetFill = target.getAttribute('fill') || window.getComputedStyle(target).fill;
        
        const allBars = chartRef.current.querySelectorAll('.MuiBarElement-root');
        
        // Entferne alle bestehenden highlights
        allBars.forEach(bar => bar.classList.remove('highlighted'));
        
        // Füge highlight zu allen Balken mit der GLEICHEN FARBE hinzu
        allBars.forEach((bar) => {
          const barFill = bar.getAttribute('fill') || window.getComputedStyle(bar).fill;
          if (barFill === targetFill) {
            bar.classList.add('highlighted');
          }
        });
        
        // Setze den highlightedSeries State (für CSS opacity)
        setHighlightedSeries(targetFill);
      };

      const handleBarMouseLeave = () => {
        // Entferne alle highlights
        const allBars = chartRef.current.querySelectorAll('.MuiBarElement-root');
        allBars.forEach(bar => bar.classList.remove('highlighted'));
        setHighlightedSeries(null);
      };

      // Find all bar elements
      const bars = chartRef.current.querySelectorAll('.MuiBarElement-root');
      console.log('=== Found bars:', bars.length);
      bars.forEach(bar => {
        bar.addEventListener('mouseenter', handleBarMouseEnter);
        bar.addEventListener('mouseleave', handleBarMouseLeave);
      });

      return () => {
        bars.forEach(bar => {
          bar.removeEventListener('mouseenter', handleBarMouseEnter);
          bar.removeEventListener('mouseleave', handleBarMouseLeave);
        });
      };
    }, 100);

    return () => clearTimeout(timeout);
  }, [fallzahlenData, showIndicationApprovals, showMyReports, showForeignSignedReports, showReleasedReports]);

  // Shift + Mausrad zum Wechseln der Zeiträume
  useEffect(() => {
    const handleWheel = (event) => {
      if (event.shiftKey && (startPeriod || endPeriod)) {
        event.preventDefault();
        
        const direction = event.deltaY > 0 ? -1 : 1; // Down = previous, Up = next
        
        setStartPeriod(prev => getAdjacentPeriod(prev, timeFilter, direction));
        setEndPeriod(prev => getAdjacentPeriod(prev, timeFilter, direction));
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [timeFilter, startPeriod, endPeriod]);

  const loadUsers = async () => {
    try {
      const response = await fetch(createApiUrl('users/'), {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter nur AKTIVE User aus Gruppe 1-5
        const filteredUsers = data.filter(user => 
          user.group_level >= 1 && 
          user.group_level <= 5 && 
          user.is_active === true
        );
        setAllUsers(filteredUsers);
        // Wähle alle gefilterten User standardmäßig aus
        setSelectedUsers(filteredUsers);
      } else {
        showError(t('statistics.team.loadUsersError', 'Fehler beim Laden der Benutzer'), 'ERR-STATS-TEAM-001');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showError(t('statistics.team.loadUsersError', 'Fehler beim Laden der Benutzer'), 'ERR-STATS-TEAM-002');
    }
  };

  const loadFallzahlenData = async () => {
    if (selectedUsers.length === 0) return;
    
    setFallzahlenLoading(true);
    try {
      // Erstelle User-IDs String für die API
      const userIds = selectedUsers.map(u => u.id).join(',');
      
      const response = await fetch(createApiUrl(`statistics/team/fallzahlen?user_ids=${userIds}`), {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('=== TEAM FALLZAHLEN: Received data:', data);
        console.log('=== TEAM FALLZAHLEN: User data keys:', Object.keys(data.users || {}));
        setFallzahlenData(data.users); // Backend returns { "success": true, "users": result }
      } else {
        console.error('=== TEAM FALLZAHLEN: Response not OK:', response.status, response.statusText);
        showError(t('statistics.team.fallzahlen.loadError', 'Fehler beim Laden der Fallzahlen'), 'ERR-STATS-TEAM-003');
      }
    } catch (error) {
      console.error('Error loading Fallzahlen data:', error);
      showError(t('statistics.team.fallzahlen.loadError', 'Fehler beim Laden der Fallzahlen'), 'ERR-STATS-TEAM-004');
    } finally {
      setFallzahlenLoading(false);
    }
  };

  const handleTimeFilterChange = (event) => {
    const newFilterType = event.target.value;
    setTimeFilter(newFilterType);
    // Setze die Perioden auf den aktuellen Wert für den neuen Filter
    const currentPeriod = getCurrentPeriod(newFilterType);
    setStartPeriod(currentPeriod);
    setEndPeriod(currentPeriod);
  };

  const handleStartPeriodChange = (event) => {
    setStartPeriod(event.target.value);
  };

  const handleEndPeriodChange = (event) => {
    setEndPeriod(event.target.value);
  };

  // CSV Export Function
  const handleCsvDownload = () => {
    if (!fallzahlenData || selectedUsers.length === 0) return;

    const formatPeriod = (period) => {
      if (!period) return period;
      try {
        switch (timeFilter) {
          case 'date':
            if (!isFormatConfigLoaded) return period;
            return formatDateSync(period);
          case 'week':
            const weekMatch = period.match(/(\d{4})-W(\d{2})/);
            if (weekMatch) return `KW${weekMatch[2]} - ${weekMatch[1]}`;
            return period;
          case 'month':
            const monthMatch = period.match(/(\d{4})-(\d{2})/);
            if (monthMatch) return `${monthMatch[2]}.${monthMatch[1]}`;
            return period;
          case 'quarter':
            const quarterMatch = period.match(/(\d{4})-Q(\d)/);
            if (quarterMatch) return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
            return period;
          case 'year':
            return period;
          default:
            return period;
        }
      } catch (error) {
        return period;
      }
    };

    // Sammle alle Perioden aus den fallzahlenData
    const allPeriods = new Set();
    selectedUsers.forEach(user => {
      const userData = fallzahlenData[user.id];
      if (!userData) return;

      let dataArray = [];
      switch (timeFilter) {
        case 'date': dataArray = userData.indication_approvals?.by_date || []; break;
        case 'week': dataArray = userData.indication_approvals?.by_week || []; break;
        case 'month': dataArray = userData.indication_approvals?.by_month || []; break;
        case 'quarter': dataArray = userData.indication_approvals?.by_quarter || []; break;
        case 'year': dataArray = userData.indication_approvals?.by_year || []; break;
      }
      
      dataArray.forEach(item => allPeriods.add(item.period));
      
      // Auch andere Kategorien durchsuchen
      ['my_reports', 'foreign_signed_reports', 'released_reports'].forEach(categoryKey => {
        switch (timeFilter) {
          case 'date': dataArray = userData[categoryKey]?.by_date || []; break;
          case 'week': dataArray = userData[categoryKey]?.by_week || []; break;
          case 'month': dataArray = userData[categoryKey]?.by_month || []; break;
          case 'quarter': dataArray = userData[categoryKey]?.by_quarter || []; break;
          case 'year': dataArray = userData[categoryKey]?.by_year || []; break;
        }
        dataArray.forEach(item => allPeriods.add(item.period));
      });
    });

    // Filter nach Zeitraum und sortiere
    let filteredPeriods = Array.from(allPeriods).sort();
    if (startPeriod || endPeriod) {
      filteredPeriods = filteredPeriods.filter(period => {
        if (startPeriod && endPeriod) {
          return period >= startPeriod && period <= endPeriod;
        } else if (startPeriod) {
          return period >= startPeriod;
        } else if (endPeriod) {
          return period <= endPeriod;
        }
        return true;
      });
    }

    // Build CSV Header - Zeitraum + für jeden User 4 Spalten
    let csv = '\uFEFF'; // BOM for Excel UTF-8
    csv += '"' + t('statistics.personal.fallzahlen.timePeriod', 'Zeitraum') + '";';
    
    selectedUsers.forEach(user => {
      const userName = `${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim();
      if (showIndicationApprovals) csv += `"${userName} - ${t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen')}";`;
      if (showMyReports) csv += `"${userName} - ${t('statistics.team.fallzahlen.createdReports', 'Erstellte Befunde')}";`;
      if (showForeignSignedReports) csv += `"${userName} - ${t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde')}";`;
      if (showReleasedReports) csv += `"${userName} - ${t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde')}";`;
    });
    
    csv = csv.slice(0, -1) + '\n'; // Entferne das letzte Semikolon und füge Newline hinzu

    // Build CSV Data - Für jede Periode eine Zeile
    filteredPeriods.forEach(period => {
      csv += '="' + formatPeriod(period) + '";';
      
      selectedUsers.forEach(user => {
        const userData = fallzahlenData[user.id];
        if (!userData) {
          if (showIndicationApprovals) csv += '0;';
          if (showMyReports) csv += '0;';
          if (showForeignSignedReports) csv += '0;';
          if (showReleasedReports) csv += '0;';
          return;
        }

        // Hilfsfunktion zum Finden des Counts für eine Periode
        const getCountForPeriod = (categoryKey) => {
          let dataArray = [];
          switch (timeFilter) {
            case 'date': dataArray = userData[categoryKey]?.by_date || []; break;
            case 'week': dataArray = userData[categoryKey]?.by_week || []; break;
            case 'month': dataArray = userData[categoryKey]?.by_month || []; break;
            case 'quarter': dataArray = userData[categoryKey]?.by_quarter || []; break;
            case 'year': dataArray = userData[categoryKey]?.by_year || []; break;
          }
          
          const item = dataArray.find(d => d.period === period);
          return item ? item.count : 0;
        };

        if (showIndicationApprovals) csv += getCountForPeriod('indication_approvals') + ';';
        if (showMyReports) csv += getCountForPeriod('my_reports') + ';';
        if (showForeignSignedReports) csv += getCountForPeriod('foreign_signed_reports') + ';';
        if (showReleasedReports) csv += getCountForPeriod('released_reports') + ';';
      });
      
      csv = csv.slice(0, -1) + '\n'; // Entferne das letzte Semikolon und füge Newline hinzu
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `team_fallzahlen_statistics.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF Export Function
  const handlePdfDownload = async () => {
    if (!fallzahlenData || !chartData || chartData.length === 0) return;

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Hole Benutzerinformationen aus localStorage
      const userStr = localStorage.getItem('user');
      const currentUser = userStr ? JSON.parse(userStr) : null;
      const userName = currentUser 
        ? `${currentUser.academic_degree || ''} ${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
        : '';
      
      let yPosition = 15;

      const formatPeriod = (period) => {
        if (!period) return '';
        try {
          switch (timeFilter) {
            case 'date':
              if (!isFormatConfigLoaded) return period;
              return formatDateSync(period);
            case 'week':
              const weekMatch = period.match(/(\d{4})-W(\d{2})/);
              if (weekMatch) return `KW${weekMatch[2]} - ${weekMatch[1]}`;
              return period;
            case 'month':
              const monthMatch = period.match(/(\d{4})-(\d{2})/);
              if (monthMatch) return `${monthMatch[2]}.${monthMatch[1]}`;
              return period;
            case 'quarter':
              const quarterMatch = period.match(/(\d{4})-Q(\d)/);
              if (quarterMatch) return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
              return period;
            case 'year':
              return period;
            default:
              return period;
          }
        } catch (error) {
          return period;
        }
      };

      // Funktion für Fußzeile
      const addFooter = () => {
        const now = new Date();
        const dateStr = isFormatConfigLoaded ? formatDateSync(now.toISOString().split('T')[0]) : now.toLocaleDateString();
        const timeStr = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        const footerText = `Team Report - Fallzahlen, ${t('statistics.personal.pdf.createdBy', 'erstellt von')} ${userName}, ${t('statistics.personal.pdf.on', 'am')} ${dateStr} ${t('statistics.personal.pdf.at', 'um')} ${timeStr}`;
        
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(footerText, 14, pageHeight - 10);
        pdf.setTextColor(0, 0, 0);
      };

      // Titel (linkszentriert)
      pdf.setFontSize(16);
      pdf.text('Team Report - Fallzahlen', 14, yPosition);
      yPosition += 10;

      // Zeitraum-Info
      pdf.setFontSize(10);
      const timeFilterLabels = {
        'date': t('statistics.team.fallzahlen.day', 'Tag'),
        'week': t('statistics.team.fallzahlen.week', 'Woche'),
        'month': t('statistics.team.fallzahlen.month', 'Monat'),
        'quarter': t('statistics.team.fallzahlen.quarter', 'Quartal'),
        'year': t('statistics.team.fallzahlen.year', 'Jahr')
      };
      const filterText = `${t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}: ${timeFilterLabels[timeFilter] || timeFilter}`;
      pdf.text(filterText, 14, yPosition);
      yPosition += 5;
      
      if (startPeriod || endPeriod) {
        const rangeText = `${startPeriod ? formatPeriod(startPeriod) : t('statistics.team.fallzahlen.all', 'Alle')} - ${endPeriod ? formatPeriod(endPeriod) : t('statistics.team.fallzahlen.all', 'Alle')}`;
        pdf.text(rangeText, 14, yPosition);
        yPosition += 5;
      }
      
      yPosition += 5;

      // Sammle alle verfügbaren Perioden
      const allPeriods = new Set();
      Object.values(fallzahlenData).forEach(userData => {
        if (!userData) return;
        
        const addPeriods = (data) => {
          if (data) {
            data.forEach(d => allPeriods.add(d.period));
          }
        };
        
        switch (timeFilter) {
          case 'date':
            addPeriods(userData.my_reports?.by_date);
            addPeriods(userData.foreign_signed_reports?.by_date);
            addPeriods(userData.indication_approvals?.by_date);
            addPeriods(userData.released_reports?.by_date);
            break;
          case 'week':
            addPeriods(userData.my_reports?.by_week);
            addPeriods(userData.foreign_signed_reports?.by_week);
            addPeriods(userData.indication_approvals?.by_week);
            addPeriods(userData.released_reports?.by_week);
            break;
          case 'month':
            addPeriods(userData.my_reports?.by_month);
            addPeriods(userData.foreign_signed_reports?.by_month);
            addPeriods(userData.indication_approvals?.by_month);
            addPeriods(userData.released_reports?.by_month);
            break;
          case 'quarter':
            addPeriods(userData.my_reports?.by_quarter);
            addPeriods(userData.foreign_signed_reports?.by_quarter);
            addPeriods(userData.indication_approvals?.by_quarter);
            addPeriods(userData.released_reports?.by_quarter);
            break;
          case 'year':
            addPeriods(userData.my_reports?.by_year);
            addPeriods(userData.foreign_signed_reports?.by_year);
            addPeriods(userData.indication_approvals?.by_year);
            addPeriods(userData.released_reports?.by_year);
            break;
        }
      });
      
      // Sortiere Perioden
      let periodsArray = Array.from(allPeriods).sort();
      
      // Filter nach Zeitraum
      if (startPeriod || endPeriod) {
        periodsArray = periodsArray.filter(period => {
          if (startPeriod && endPeriod) {
            return period >= startPeriod && period <= endPeriod;
          } else if (startPeriod) {
            return period >= startPeriod;
          } else if (endPeriod) {
            return period <= endPeriod;
          }
          return true;
        });
      }

      // Für jeden User eine separate Tabelle erstellen
      selectedUsers.forEach((user, userIndex) => {
        const userData = fallzahlenData[user.id];
        if (!userData) return;
        
        // Prüfe ob neue Seite benötigt wird
        if (yPosition > 250) {
          addFooter();
          pdf.addPage();
          yPosition = 15;
        }
        
        // User-Name als Überschrift
        pdf.setFontSize(12);
        const userTableName = `${user.academic_degree || ''} ${user.first_name} ${user.last_name}`.trim();
        pdf.text(userTableName, 14, yPosition);
        yPosition += 7;
        
        // Tabellen-Header basierend auf aktivierten Kategorien
      const tableHeaders = [t('statistics.personal.fallzahlen.timePeriod', 'Zeitraum')];
        if (showIndicationApprovals) tableHeaders.push(t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen'));
        if (showMyReports) tableHeaders.push(t('statistics.team.fallzahlen.createdReports', 'Erstellte Befunde'));
        if (showForeignSignedReports) tableHeaders.push(t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde'));
        if (showReleasedReports) tableHeaders.push(t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde'));
        
        // Hole die Daten für diesen User
        const getUserDataForPeriod = (category, period) => {
          let data = [];
          switch (timeFilter) {
            case 'date': data = userData[category]?.by_date || []; break;
            case 'week': data = userData[category]?.by_week || []; break;
            case 'month': data = userData[category]?.by_month || []; break;
            case 'quarter': data = userData[category]?.by_quarter || []; break;
            case 'year': data = userData[category]?.by_year || []; break;
          }
          const item = data.find(d => d.period === period);
          return item ? item.count : 0;
        };
        
        // Tabellen-Daten für diesen User - eine Zeile pro Periode
        const tableData = periodsArray.map(period => {
          const rowData = [formatPeriod(period)];
          if (showIndicationApprovals) rowData.push(getUserDataForPeriod('indication_approvals', period));
          if (showMyReports) rowData.push(getUserDataForPeriod('my_reports', period));
          if (showForeignSignedReports) rowData.push(getUserDataForPeriod('foreign_signed_reports', period));
          if (showReleasedReports) rowData.push(getUserDataForPeriod('released_reports', period));
        return rowData;
      });

      pdf.autoTable({
        head: [tableHeaders],
        body: tableData,
        startY: yPosition,
          margin: { left: 14, right: 14 },
          theme: 'grid',
        styles: { 
            fontSize: 9,
            cellPadding: 2
        },
        headStyles: {
            fillColor: [66, 66, 66],
            textColor: [255, 255, 255],
            fontStyle: 'bold'
          }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 10;
      });

      // Fußzeile auf der letzten Seite
        addFooter();

      pdf.save(`team_cases_report.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError(t('statistics.personal.pdf.error', 'Fehler beim Erstellen der PDF'), 'ERR-PDF-001');
    }
  };

  // Prüfe ob User ausgewählt sind
  if (selectedUsers.length === 0) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {t('statistics.team.categories.fallzahlen', 'Team-Fallzahlen')}
        </Typography>

        {/* Filter und User-Auswahl in einer Reihe */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
          <FormControl sx={{ width: 120, flexShrink: 0 }} disabled>
            <InputLabel>{t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}</InputLabel>
            <Select
              value={timeFilter}
              onChange={handleTimeFilterChange}
              label={t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}
            >
              <MenuItem value="date">{t('statistics.team.fallzahlen.day', 'Tag')}</MenuItem>
              <MenuItem value="week">{t('statistics.team.fallzahlen.week', 'Woche')}</MenuItem>
              <MenuItem value="month">{t('statistics.team.fallzahlen.month', 'Monat')}</MenuItem>
              <MenuItem value="quarter">{t('statistics.team.fallzahlen.quarter', 'Quartal')}</MenuItem>
              <MenuItem value="year">{t('statistics.team.fallzahlen.year', 'Jahr')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ width: 120, flexShrink: 0 }} disabled>
            <InputLabel>{t('statistics.team.fallzahlen.startPeriod', 'Von')}</InputLabel>
            <Select
              value={startPeriod}
              label={t('statistics.team.fallzahlen.startPeriod', 'Von')}
            >
              <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ width: 120, flexShrink: 0 }} disabled>
            <InputLabel>{t('statistics.team.fallzahlen.endPeriod', 'Bis')}</InputLabel>
            <Select
              value={endPeriod}
              label={t('statistics.team.fallzahlen.endPeriod', 'Bis')}
            >
              <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={allUsers}
            value={selectedUsers}
            onChange={(event, newValue) => {
              setSelectedUsers(newValue);
            }}
            getOptionLabel={(user) => `${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('statistics.team.selectUsers', 'Benutzer auswählen')}
                placeholder={t('statistics.team.selectUsersPlaceholder', 'Tippen zum Suchen...')}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((user, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={user.id}
                  label={`${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
                  size="small"
                />
              ))
            }
            sx={{ flex: 1, minWidth: 250 }}
          />
        </Box>

        <Typography variant="body2" color="text.secondary">
          {t('statistics.team.selectUsersFirst', 'Bitte wählen Sie mindestens einen Benutzer aus.')}
        </Typography>
      </Box>
    );
  }

  if (fallzahlenLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!fallzahlenData) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('statistics.team.fallzahlen.noData', 'Keine Daten verfügbar')}
        </Typography>
      </Box>
    );
  }

  const getChartData = () => {
    if (!fallzahlenData || Object.keys(fallzahlenData).length === 0) return [];

    // Für Team-Statistiken: Erstelle ein Dataset pro User mit aggregierten Daten über den Zeitraum
    const chartData = [];

    selectedUsers.forEach(user => {
      const userData = fallzahlenData[user.id];
      if (!userData) return;

      // Formatiere den Namen mehrzeilig: Titel in erster Zeile, Name in zweiter
      const title = user.academic_degree || '';
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      const userName = title ? `${title}\n${fullName}` : fullName;

      // Berechne Total Counts für den gewählten Zeitraum
      const getCategoryTotal = (categoryKey) => {
        let data = [];
        switch (timeFilter) {
          case 'date': data = userData[categoryKey].by_date; break;
          case 'week': data = userData[categoryKey].by_week; break;
          case 'month': data = userData[categoryKey].by_month; break;
          case 'quarter': data = userData[categoryKey].by_quarter; break;
          case 'year': data = userData[categoryKey].by_year; break;
          default: break;
        }

        // Filter nach Zeitraum
        if (startPeriod || endPeriod) {
          data = data.filter(item => {
            if (startPeriod && endPeriod) {
              return item.period >= startPeriod && item.period <= endPeriod;
            } else if (startPeriod) {
              return item.period >= startPeriod;
            } else if (endPeriod) {
              return item.period <= endPeriod;
            }
            return true;
          });
        }

        return data.reduce((sum, item) => sum + item.count, 0);
      };

      chartData.push({
        user: userName,
        userId: user.id,
        indicationCount: getCategoryTotal('indication_approvals'),
        myReportsCount: getCategoryTotal('my_reports'),
        foreignSignedCount: getCategoryTotal('foreign_signed_reports'),
        releasedCount: getCategoryTotal('released_reports')
      });
    });

    return chartData;
  };

  const chartData = getChartData();
  
  const availablePeriods = (() => {
    const getAllPeriods = () => {
      const periods = new Set();
      
      if (!fallzahlenData) return [];

      Object.values(fallzahlenData).forEach(userData => {
        // Überprüfe ob userData gültig ist und alle nötigen Kategorien hat
        if (!userData || !userData.my_reports || !userData.foreign_signed_reports || 
            !userData.indication_approvals || !userData.released_reports) {
          return;
        }

        const addPeriods = (data) => {
          if (data) {
            data.forEach(d => periods.add(d.period));
          }
        };
        
        switch (timeFilter) {
          case 'date':
            addPeriods(userData.my_reports.by_date);
            addPeriods(userData.foreign_signed_reports.by_date);
            addPeriods(userData.indication_approvals.by_date);
            addPeriods(userData.released_reports.by_date);
            break;
          case 'week':
            addPeriods(userData.my_reports.by_week);
            addPeriods(userData.foreign_signed_reports.by_week);
            addPeriods(userData.indication_approvals.by_week);
            addPeriods(userData.released_reports.by_week);
            break;
          case 'month':
            addPeriods(userData.my_reports.by_month);
            addPeriods(userData.foreign_signed_reports.by_month);
            addPeriods(userData.indication_approvals.by_month);
            addPeriods(userData.released_reports.by_month);
            break;
          case 'quarter':
            addPeriods(userData.my_reports.by_quarter);
            addPeriods(userData.foreign_signed_reports.by_quarter);
            addPeriods(userData.indication_approvals.by_quarter);
            addPeriods(userData.released_reports.by_quarter);
            break;
          case 'year':
            addPeriods(userData.my_reports.by_year);
            addPeriods(userData.foreign_signed_reports.by_year);
            addPeriods(userData.indication_approvals.by_year);
            addPeriods(userData.released_reports.by_year);
            break;
          default:
            return [];
        }
      });
      
      return Array.from(periods).sort().reverse();
    };
    
    return getAllPeriods();
  })();

  const formatPeriod = (period) => {
    if (!period) return period;
    
    try {
      switch (timeFilter) {
        case 'date':
          if (!isFormatConfigLoaded) return period;
          return formatDateSync(period);
        
        case 'week':
          const weekMatch = period.match(/(\d{4})-W(\d{2})/);
          if (weekMatch) {
            return `KW${weekMatch[2]} - ${weekMatch[1]}`;
          }
          return period;
        
        case 'month':
          const monthMatch = period.match(/(\d{4})-(\d{2})/);
          if (monthMatch) {
            return `${monthMatch[2]}.${monthMatch[1]}`;
          }
          return period;
        
        case 'quarter':
          const quarterMatch = period.match(/(\d{4})-Q(\d)/);
          if (quarterMatch) {
            return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
          }
          return period;
        
        case 'year':
          return period;
        
        default:
          return period;
      }
    } catch (error) {
      console.error('Error formatting period:', error);
      return period;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('statistics.team.categories.fallzahlen', 'Team-Fallzahlen')}
      </Typography>

      {/* Filter und User-Auswahl in einer Reihe */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
        <FormControl sx={{ width: 120, flexShrink: 0 }}>
          <InputLabel>{t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}</InputLabel>
          <Select
            value={timeFilter}
            onChange={handleTimeFilterChange}
            label={t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}
          >
            <MenuItem value="date">{t('statistics.team.fallzahlen.day', 'Tag')}</MenuItem>
            <MenuItem value="week">{t('statistics.team.fallzahlen.week', 'Woche')}</MenuItem>
            <MenuItem value="month">{t('statistics.team.fallzahlen.month', 'Monat')}</MenuItem>
            <MenuItem value="quarter">{t('statistics.team.fallzahlen.quarter', 'Quartal')}</MenuItem>
            <MenuItem value="year">{t('statistics.team.fallzahlen.year', 'Jahr')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ width: 120, flexShrink: 0 }}>
          <InputLabel>{t('statistics.team.fallzahlen.startPeriod', 'Von')}</InputLabel>
          <Select
            value={startPeriod}
            onChange={handleStartPeriodChange}
            label={t('statistics.team.fallzahlen.startPeriod', 'Von')}
          >
            <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
            {availablePeriods.map((period) => (
              <MenuItem key={period} value={period}>
                {formatPeriod(period)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ width: 120, flexShrink: 0 }}>
          <InputLabel>{t('statistics.team.fallzahlen.endPeriod', 'Bis')}</InputLabel>
          <Select
            value={endPeriod}
            onChange={handleEndPeriodChange}
            label={t('statistics.team.fallzahlen.endPeriod', 'Bis')}
          >
            <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
            {availablePeriods.map((period) => (
              <MenuItem key={period} value={period}>
                {formatPeriod(period)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          multiple
          disableCloseOnSelect
          options={allUsers}
          value={selectedUsers}
          onChange={(event, newValue) => {
            setSelectedUsers(newValue);
          }}
          getOptionLabel={(user) => `${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('statistics.team.selectUsers', 'Benutzer auswählen')}
              placeholder={t('statistics.team.selectUsersPlaceholder', 'Tippen zum Suchen...')}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((user, index) => (
              <Chip
                {...getTagProps({ index })}
                key={user.id}
                label={`${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
                size="small"
              />
            ))
          }
          sx={{ flex: 1, minWidth: 250 }}
        />
      </Box>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {t('statistics.personal.fallzahlen.chartTitle', 'Entwicklung der Fallzahlen')}
          {' '}
          {timeFilter === 'date' && t('statistics.personal.fallzahlen.perDay', 'pro Tag')}
          {timeFilter === 'week' && t('statistics.personal.fallzahlen.perWeek', 'pro Woche')}
          {timeFilter === 'month' && t('statistics.personal.fallzahlen.perMonth', 'pro Monat')}
          {timeFilter === 'quarter' && t('statistics.personal.fallzahlen.perQuarter', 'pro Quartal')}
          {timeFilter === 'year' && t('statistics.personal.fallzahlen.perYear', 'pro Jahr')}
        </Typography>
        
        {!showMyReports && !showForeignSignedReports && !showIndicationApprovals && !showReleasedReports ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('statistics.personal.fallzahlen.noSeriesSelected', 'Bitte wählen Sie mindestens eine Kategorie aus')}
          </Typography>
        ) : chartData.length > 0 ? (
          <Box sx={{ display: 'flex', width: '100%', position: 'relative' }}>
            {/* Custom Y-Achsen Labels - Links vom Chart */}
            <Box sx={{ 
              width: '200px', 
              height: Math.max(400, selectedUsers.length * 100),
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
              pt: '15px', // Chart margin top
              pb: '70px' // Chart margin bottom
            }}>
              {selectedUsers.map((user, index) => {
                // Berechne die tatsächliche Y-Achsen-Höhe (ohne top/bottom margins)
                const totalHeight = Math.max(400, selectedUsers.length * 100);
                const chartMarginTop = 20;
                const chartMarginBottom = 50;
                const yAxisHeight = totalHeight - chartMarginTop - chartMarginBottom;
                const userHeight = yAxisHeight / selectedUsers.length;
                
                return (
                  <Box 
                    key={user.id}
                    sx={{ 
                      height: `${userHeight}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      pr: 2,
                      borderBottom: index < selectedUsers.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        textAlign: 'right',
                        fontSize: '0.875rem',
                        lineHeight: 1.3
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 600, display: 'block' }}>
                        {user.academic_degree || ''}
                      </Box>
                      <Box component="span" sx={{ display: 'block' }}>
                        {user.first_name} {user.last_name}
                      </Box>
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Chart ohne Y-Achsen Labels */}
            <Box ref={chartRef} sx={{ flex: 1, height: Math.max(400, selectedUsers.length * 100) }}>
              <BarChart
                dataset={chartData}
                layout="horizontal"
                yAxis={[{ 
                  dataKey: 'user',
                  scaleType: 'band',
                  categoryGapRatio: 0.3,
                  barGapRatio: 0.1
                }]}
                xAxis={[{
                  min: 0,
                  max: (() => {
                    // Finde den maximalen Wert aus allen aktiven Serien
                    const maxValue = Math.max(
                      ...chartData.map(d => Math.max(
                        showIndicationApprovals ? d.indicationCount : 0,
                        showMyReports ? d.myReportsCount : 0,
                        showForeignSignedReports ? d.foreignSignedCount : 0,
                        showReleasedReports ? d.releasedCount : 0
                      ))
                    );
                    // Füge 10% Puffer hinzu
                    return Math.ceil(maxValue * 1.1);
                  })()
                }]}
                tooltip={{
                  trigger: 'item'
                }}
                onItemClick={(event, d) => {
                  console.log('=== ITEM CLICK:', d);
                }}
                sx={{
                  '.MuiChartsLegend-root': {
                    display: 'none !important'
                  },
                  '.MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
                    display: 'none !important'
                  },
                  '.MuiChartsAxis-left .MuiChartsAxis-line': {
                    display: 'none !important'
                  },
                  // Custom hover effect - SEHR dunkles Dimming für nicht-highlighted Balken
                  '& .MuiBarElement-root': {
                    transition: 'opacity 0.2s ease-in-out, filter 0.2s ease-in-out',
                    cursor: 'pointer',
                    ...(highlightedSeries && {
                      opacity: 0.15,
                      filter: 'grayscale(80%)'
                    })
                  },
                  // Highlighted series: CSS-Klasse wird dynamisch über JavaScript gesetzt
                  '& .MuiBarElement-root.highlighted': {
                    opacity: '1 !important',
                    filter: 'none !important'
                  }
                }}
                series={[
                  ...(showIndicationApprovals ? [{
                    type: 'bar',
                    id: 'indicationCount',
                    dataKey: 'indicationCount',
                    label: t('statistics.team.fallzahlen.indicationApprovals', 'Indikationsprüfungen'),
                    color: 'var(--rd-chart-1)',
                    valueFormatter: (value) => `${value}`
                  }] : []),
                  ...(showMyReports ? [{
                    type: 'bar',
                    id: 'myReportsCount',
                    dataKey: 'myReportsCount',
                    label: t('statistics.team.fallzahlen.createdReports', 'Erstellte Befunde'),
                    color: 'var(--rd-chart-2)',
                    valueFormatter: (value) => `${value}`
                  }] : []),
                  ...(showForeignSignedReports ? [{
                    type: 'bar',
                    id: 'foreignSignedCount',
                    dataKey: 'foreignSignedCount',
                    label: t('statistics.team.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde'),
                    color: 'var(--rd-chart-3)',
                    valueFormatter: (value) => `${value}`
                  }] : []),
                  ...(showReleasedReports ? [{
                    type: 'bar',
                    id: 'releasedCount',
                    dataKey: 'releasedCount',
                    label: t('statistics.team.fallzahlen.releasedReports', 'Freigegebene Befunde'),
                    color: 'var(--rd-chart-4)',
                    valueFormatter: (value) => `${value}`
                  }] : [])
                ]}
                margin={{ top: 20, right: 30, bottom: 50, left: 10 }}
                grid={{ vertical: true, horizontal: false }}
              />
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('statistics.personal.fallzahlen.noChartData', 'Keine Daten für die ausgewählten Filter verfügbar')}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={showIndicationApprovals} 
                onChange={(e) => setShowIndicationApprovals(e.target.checked)}
                sx={{ color: 'var(--rd-chart-1)', '&.Mui-checked': { color: 'var(--rd-chart-1)' } }}
              />
            }
            label={t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen')}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showMyReports} 
                onChange={(e) => setShowMyReports(e.target.checked)}
                sx={{ color: 'var(--rd-chart-2)', '&.Mui-checked': { color: 'var(--rd-chart-2)' } }}
              />
            }
            label={t('statistics.team.fallzahlen.createdReports', 'Erstellte Befunde')}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showForeignSignedReports} 
                onChange={(e) => setShowForeignSignedReports(e.target.checked)}
                sx={{ color: 'var(--rd-chart-3)', '&.Mui-checked': { color: 'var(--rd-chart-3)' } }}
              />
            }
            label={t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signierte Befunde')}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={showReleasedReports} 
                onChange={(e) => setShowReleasedReports(e.target.checked)}
                sx={{ color: 'var(--rd-chart-4)', '&.Mui-checked': { color: 'var(--rd-chart-4)' } }}
              />
            }
            label={t('statistics.personal.fallzahlen.releasedReports', 'Freigegebene Befunde')}
          />
        </Box>
      </Paper>
    </Box>
  );
});

Cases.displayName = 'Cases';

export default Cases;
